import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { ADJUSTMENT_REASONS, fmtQty, type StockUnitRecord, type WarehouseRecord } from "@/types/inventory";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

type AdjType = "DECREASE" | "SCRAP" | "INCREASE";

interface Props {
  open: boolean;
  /** Rolls picked in the list; with none, the dialog is for adding found stock */
  units: StockUnitRecord[];
  onClose: () => void;
  onSaved: () => void;
}

/** Correct stock with a reason: take part off a roll, scrap whole rolls, or add stock that was found */
export function AdjustDialog({ open, units, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const [type, setType] = useState<AdjType>("DECREASE");
  const [reason, setReason] = useState("DAMAGE");
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState<Record<string, string>>({});
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [found, setFound] = useState({ warehouse_id: "", product_id: "", product_label: "", variant_id: "", lot_number: "", unit: "", unit_no: "", quantity: "" });
  const [variants, setVariants] = useState<Array<{ id: string; name: string }>>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(units.length ? "DECREASE" : "INCREASE");
    setReason(units.length ? "DAMAGE" : "EXCESS_FOUND");
    setNotes("");
    setQty({});
    setVariants([]);
    api
      .get("/api/Warehouse/List")
      .then((r) => {
        const list = unwrapArray<WarehouseRecord>(r);
        setWarehouses(list);
        setFound({ warehouse_id: list.find((w) => w.is_default)?.id || list[0]?.id || "", product_id: "", product_label: "", variant_id: "", lot_number: "", unit: "", unit_no: "", quantity: "" });
      })
      .catch(() => setWarehouses([]));
  }, [open, units]);

  const pickProduct = async (id: string, row: any) => {
    setFound((f) => ({ ...f, product_id: id, product_label: row ? `${row.name} (${row.code})` : "", unit: row?.unit || "", variant_id: "" }));
    setVariants([]);
    if (!id) return;
    try {
      setVariants(unwrapArray<any>(await api.get(`/api/Product/Variants/${id}`)).map((v) => ({ id: v.id, name: v.name })));
    } catch {
      /* no shades to choose */
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    let items: any[] = [];
    if (type === "INCREASE") {
      if (!found.product_id) return error("Validation Error", "Choose the product");
      if (!found.unit_no.trim() || !(Number(found.quantity) > 0)) return error("Validation Error", "Enter the roll number and its quantity");
      items = [{ product_id: found.product_id, variant_id: found.variant_id || undefined, lot_number: found.lot_number.trim() || undefined, unit: found.unit || undefined, unit_no: found.unit_no.trim(), quantity: Number(found.quantity) }];
    } else if (type === "DECREASE") {
      items = units.filter((u) => Number(qty[u.id]) > 0).map((u) => ({ unit_id: u.id, quantity: Number(qty[u.id]) }));
      if (!items.length) return error("Validation Error", "Enter how much to take off at least one roll");
    } else {
      items = units.map((u) => ({ unit_id: u.id }));
    }
    if (reason === "OTHER" && !notes.trim()) return error("Validation Error", "Say what happened when the reason is Other");
    setSaving(true);
    try {
      await api.post("/api/Inventory/Adjust", {
        adjustment_type: type,
        reason,
        notes: notes.trim() || undefined,
        warehouse_id: type === "INCREASE" ? found.warehouse_id : undefined,
        items,
      });
      success("Stock adjusted", "The change is in the stock ledger");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not adjust", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Adjust stock</DialogTitle>
          <DialogDescription>Every adjustment needs a reason and is kept in the stock ledger.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col min-h-0">
          <div className="space-y-4 py-2 text-sm overflow-y-auto max-h-[64vh] pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>What happened</Label>
                <select className={field} value={type} onChange={(e) => setType(e.target.value as AdjType)}>
                  {units.length > 0 && <option value="DECREASE">Take quantity off the selected rolls</option>}
                  {units.length > 0 && <option value="SCRAP">Scrap the selected rolls</option>}
                  <option value="INCREASE">Add stock that was found</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Reason *</Label>
                <select className={field} value={reason} onChange={(e) => setReason(e.target.value)}>
                  {Object.entries(ADJUSTMENT_REASONS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            {type !== "INCREASE" && (
              <div className="rounded-lg border border-border divide-y divide-border">
                {units.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">Roll {u.unit_no} · {u.product?.name}{u.variant ? ` · ${u.variant.name}` : ""}</div>
                      <div className="text-muted-foreground">Lot {u.lot?.lot_number} · {u.warehouse?.name} · holds {fmtQty(u.quantity, u.unit)}</div>
                    </div>
                    {type === "DECREASE" ? (
                      <Input className="h-8 w-36 text-xs" inputMode="decimal" placeholder={`Take off (${u.unit})`} value={qty[u.id] || ""} onChange={(e) => setQty({ ...qty, [u.id]: e.target.value })} />
                    ) : (
                      <span className="text-destructive font-semibold">Scrap all</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {type === "INCREASE" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Warehouse *</Label>
                  <select className={field} value={found.warehouse_id} onChange={(e) => setFound({ ...found, warehouse_id: e.target.value })}>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Product *</Label>
                  <SearchSelect endpoint="/api/Product/List" extraParams={{ active: "true" }} value={found.product_id} selectedLabel={found.product_label} getLabel={(row) => `${row.name} (${row.code})`} placeholder="Search products" onChange={pickProduct} />
                </div>
                <div className="space-y-1.5">
                  <Label>Shade</Label>
                  <select className={field} value={found.variant_id} onChange={(e) => setFound({ ...found, variant_id: e.target.value })} disabled={!variants.length}>
                    <option value="">{variants.length ? "-- No shade --" : "No shades"}</option>
                    {variants.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Dye lot / batch</Label>
                  <Input value={found.lot_number} onChange={(e) => setFound({ ...found, lot_number: e.target.value })} placeholder="Adjustment no." className="h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label>Roll no. *</Label>
                  <Input value={found.unit_no} onChange={(e) => setFound({ ...found, unit_no: e.target.value })} className="h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label>Quantity * {found.unit && <span className="text-muted-foreground">({found.unit})</span>}</Label>
                  <Input inputMode="decimal" value={found.quantity} onChange={(e) => setFound({ ...found, quantity: e.target.value })} className="h-10" />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Notes {reason === "OTHER" && "*"}</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} variant={type === "SCRAP" ? "destructive" : "default"} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              {type === "SCRAP" ? "Scrap rolls" : type === "INCREASE" ? "Add to stock" : "Take off stock"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
